import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { ToastVariant } from '@/components/ui/Toast'

/* ==========================================================================
   Toast Context
   Provides toast state management and methods to show toasts
   ========================================================================== */

export interface ToastItem {
  id: string
  variant: ToastVariant
  message: string
  duration?: number
}

interface ToastContextValue {
  /** Array of active toasts */
  toasts: ToastItem[]
  /** Show a toast with custom variant */
  toast: (variant: ToastVariant, message: string, duration?: number) => void
  /** Show a success toast */
  success: (message: string, duration?: number) => void
  /** Show an error toast */
  error: (message: string, duration?: number) => void
  /** Show an info toast */
  info: (message: string, duration?: number) => void
  /** Remove a toast by ID */
  removeToast: (id: string) => void
  /** Remove all toasts */
  clearToasts: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let toastIdCounter = 0

function generateToastId(): string {
  toastIdCounter += 1
  return `toast-${toastIdCounter}-${Date.now()}`
}

export interface ToastProviderProps {
  children: ReactNode
  /** Maximum number of toasts to display at once (default: 5) */
  maxToasts?: number
}

/**
 * Toast provider that manages toast state and provides methods to show toasts.
 * Wrap your app with this provider to enable toast notifications.
 *
 * @example
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 */
export function ToastProvider({ children, maxToasts = 5 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const clearToasts = useCallback(() => {
    setToasts([])
  }, [])

  const toast = useCallback(
    (variant: ToastVariant, message: string, duration?: number) => {
      const newToast: ToastItem = {
        id: generateToastId(),
        variant,
        message,
        duration,
      }

      setToasts((prev) => {
        // Remove oldest toasts if we've exceeded maxToasts
        const updated = [...prev, newToast]
        if (updated.length > maxToasts) {
          return updated.slice(updated.length - maxToasts)
        }
        return updated
      })
    },
    [maxToasts]
  )

  const success = useCallback(
    (message: string, duration?: number) => {
      toast('success', message, duration)
    },
    [toast]
  )

  const error = useCallback(
    (message: string, duration?: number) => {
      toast('error', message, duration)
    },
    [toast]
  )

  const info = useCallback(
    (message: string, duration?: number) => {
      toast('info', message, duration)
    },
    [toast]
  )

  const value: ToastContextValue = {
    toasts,
    toast,
    success,
    error,
    info,
    removeToast,
    clearToasts,
  }

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

/**
 * Hook to access toast methods from any component.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { success, error, info } = useToast()
 *
 *   const handleSave = async () => {
 *     try {
 *       await saveData()
 *       success('Data saved successfully!')
 *     } catch (err) {
 *       error('Failed to save data')
 *     }
 *   }
 *
 *   return <button onClick={handleSave}>Save</button>
 * }
 * ```
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export default ToastProvider
